import { For, Show, createSignal, onMount } from "solid-js";
import "./school.css";
import { classRolesOf, classesLabel, formatDate, statusLabel, type SchoolClass, type SchoolOverview as Overview, type TeamApi } from "./team";

// Schulübersicht der Schulleitung: nur Zahlen und Namen, keine Inhalte der Klassen.
export function SchoolOverview(props: { api: TeamApi; onOpenClasses: () => void; onClassAssigned?: () => void }) {
  const [data, setData] = createSignal<Overview>();
  const [loadError, setLoadError] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [info, setInfo] = createSignal("");
  const [schoolClasses, setSchoolClasses] = createSignal<SchoolClass[]>([]);
  const [assignTarget, setAssignTarget] = createSignal<string>(""); // classId being assigned
  const [assignBusy, setAssignBusy] = createSignal(false);
  const [readerTarget, setReaderTarget] = createSignal<string>(""); // classId, für die Mitlesende gewählt werden

  const load = async () => {
    try {
      setData(await props.api.overview());
      setLoadError("");
    } catch (cause) {
      setLoadError(cause instanceof Error ? cause.message : "Die Schulübersicht konnte nicht geladen werden.");
    }
  };

  const loadClasses = async () => {
    try {
      const result = await props.api.schoolClasses();
      setSchoolClasses(result.classes.map((c) => ({
        id: c.id,
        name: c.name,
        schoolYear: c.school_year,
        teacherId: c.teacher_id,
        teacherFirst: c.teacher_first,
        teacherLast: c.teacher_last,
        studentCount: Number(c.student_count),
        readers: c.readers ?? [],
        pendingRequests: c.pending_requests ?? 0
      })));
    } catch {
      // Klassenübersicht ist optional — Fehler still ignorieren
    }
  };

  onMount(() => { void load(); void loadClasses(); });

  const assignClass = async (classId: string, teacherId: string) => {
    setAssignBusy(true);
    setError("");
    setInfo("");
    try {
      await props.api.assignClass(classId, teacherId);
      setAssignTarget("");
      setInfo("Klasse wurde zugewiesen.");
      await Promise.all([load(), loadClasses()]);
      props.onClassAssigned?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Zuweisung fehlgeschlagen.");
    } finally {
      setAssignBusy(false);
    }
  };

  const changeReaders = async (work: () => Promise<unknown>, done: string) => {
    setAssignBusy(true);
    setError("");
    setInfo("");
    try {
      await work();
      setReaderTarget("");
      setInfo(done);
      await loadClasses();
      props.onClassAssigned?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Das hat nicht geklappt.");
    } finally {
      setAssignBusy(false);
    }
  };

  const invite = async (event: SubmitEvent) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const email = String(new FormData(form).get("email") ?? "").trim();
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await props.api.invite(email);
      form.reset();
      setInfo(`Die Einladung an ${email} ist unterwegs.`);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Die Einladung konnte nicht gesendet werden.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string, email: string) => {
    setError("");
    setInfo("");
    try {
      await props.api.revoke(id);
      setInfo(`Die Einladung an ${email} wurde zurückgenommen.`);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Die Einladung konnte nicht zurückgenommen werden.");
    }
  };

  return (
    <div class="card so-card">
      <Show when={!loadError()} fallback={<p role="alert">{loadError()}</p>}>
        <Show when={data()} fallback={<div style={{ display: "flex", "justify-content": "center", padding: "30px" }}><div class="loading-spinner" /></div>}>
          {(overview) => (
            <>
              <span class={`so-badge ${overview().school.status === "trial" || overview().school.status === "active" ? "ok" : "warn"}`}>{statusLabel(overview().school)}</span>
              <h1 class="sr-title" style={{ "margin-top": "10px" }}>{overview().school.name}</h1>
              <p class="sr-sub">Ihre Schulübersicht. Die Inhalte der Klassen sehen Sie hier nicht, nur Zahlen und Ihre Lehrkräfte.</p>
              <div class="so-stats">
                <div class="so-stat"><strong>{classesLabel(overview().school)}</strong><span>{overview().school.classLimit === null ? "Klassen" : "Klassen im Test"}</span></div>
                <div class="so-stat"><strong>{overview().members.length}</strong><span>{overview().members.length === 1 ? "Lehrkraft" : "Lehrkräfte"}</span></div>
                <Show when={overview().school.status === "trial"}><div class="so-stat"><strong>{formatDate(overview().school.trialEndsAt)}</strong><span>Testende</span></div></Show>
              </div>

              <Show when={overview().school.canInvite} fallback={<div class="sr-warn" role="status">Ihre Schule ist gesperrt oder abgelaufen. Neue Einladungen sind deshalb nicht möglich. Schreiben Sie uns an mail@reflexo.info.</div>}>
                <div class="sr-legend">Lehrkraft einladen</div>
                <form class="so-invite" onSubmit={invite}>
                  <div class="form-group">
                    <label class="form-label so-hidden" for="so-email">E-Mail-Adresse der Lehrkraft</label>
                    <input id="so-email" name="email" class="form-input" type="email" required maxlength={255} placeholder="lehrkraft@ihre-schule.de" autocomplete="off" />
                  </div>
                  <button class="btn btn-pink" type="submit" disabled={busy()}>Einladung senden</button>
                </form>
                <p class="sr-hint">Die Lehrkraft bekommt eine E-Mail mit einem persönlichen Link. Er gilt 14 Tage und kann nur einmal verwendet werden.</p>
              </Show>
              <Show when={info()}><p class="so-info" role="status">{info()}</p></Show>
              <Show when={error()}><div class="form-error" style={{ "margin-top": "10px" }} role="alert">{error()}</div></Show>

              <div class="sr-legend">Lehrkräfte</div>
              <ul class="so-people">
                <For each={overview().members}>{(member) => (
                  <li class="so-person">
                    <div class="so-person-main">
                      <div class="so-person-name">
                        {member.firstName} {member.lastName}
                        <Show when={member.role === "owner"}><span class="so-pill read">Schulleitung</span></Show>
                      </div>
                      <div class="so-person-mail">{member.email}</div>
                    </div>
                    <div class="so-person-status"><span class={`so-pill ${member.active ? "" : "wait"}`}>{member.active ? "aktiv" : "gesperrt"}</span></div>
                    <div class="so-person-classes">
                      <For each={classRolesOf(member.id, schoolClasses())} fallback={<span class="so-muted">Keine Klasse</span>}>{(entry) => (
                        <div class="so-role"><span class="so-role-class">{entry.className}</span><span class={`so-pill ${entry.role === "edit" ? "" : "read"}`}>{entry.role === "edit" ? "bearbeitet" : "liest mit"}</span></div>
                      )}</For>
                    </div>
                  </li>
                )}</For>
                <For each={overview().invitations}>{(invitation) => (
                  <li class="so-person">
                    <div class="so-person-main">
                      <div class="so-person-name">{invitation.email}</div>
                      <div class="so-person-mail">Einladung gesendet</div>
                    </div>
                    <div class="so-person-status"><span class="so-pill wait">eingeladen</span></div>
                    <div class="so-person-classes">
                      <button type="button" class="sr-linkbtn" onClick={() => void revoke(invitation.id, invitation.email)}>Einladung zurücknehmen</button>
                    </div>
                  </li>
                )}</For>
              </ul>

              <Show when={schoolClasses().length > 0}>
                <div class="sr-legend" style={{ "margin-top": "24px" }}>Klassen und Lehrkräfte-Teams</div>
                <p class="sr-hint">Eine Lehrkraft bearbeitet die Klasse, weitere Lehrkräfte lesen mit und sehen alle Einträge. Beim Zuweisen liest die bisherige Lehrkraft weiter mit.</p>
                <div class="so-classes">
                  <For each={schoolClasses()}>{(cls) => (
                    <div class="so-class">
                      <div class="so-class-head">
                        <strong>{cls.name}</strong>
                        <span class="so-muted">{[cls.schoolYear, `${cls.studentCount} ${cls.studentCount === 1 ? "Schüler" : "Schüler"}`].filter(Boolean).join(" · ")}</span>
                        <Show when={cls.pendingRequests > 0}><span class="so-pill wait">Übernahme angefragt</span></Show>
                      </div>

                      <div class="so-role-row">
                        <span class="so-role-label">Bearbeitet von</span>
                        <span class="so-role-body">
                          <strong>{cls.teacherFirst} {cls.teacherLast}</strong>
                          <Show
                            when={assignTarget() === cls.id}
                            fallback={<button type="button" class="sr-linkbtn" style={{ "white-space": "nowrap" }} onClick={() => setAssignTarget(cls.id)}>ändern</button>}
                          >
                            <select
                              class="form-input so-select"
                              disabled={assignBusy()}
                              onChange={(e) => {
                                const v = e.currentTarget.value;
                                if (v) void assignClass(cls.id, v);
                              }}
                            >
                              <option value="">Lehrkraft wählen …</option>
                              <For each={overview().members.filter((m) => m.active && m.id !== cls.teacherId)}>{(m) => (
                                <option value={m.id}>{m.firstName} {m.lastName}{m.role === "owner" ? " (Schulleitung)" : ""}</option>
                              )}</For>
                            </select>
                            <button type="button" class="sr-linkbtn" onClick={() => setAssignTarget("")}>abbrechen</button>
                          </Show>
                        </span>
                      </div>

                      <div class="so-role-row">
                        <span class="so-role-label">Liest mit</span>
                        <span class="so-role-body">
                          <For each={cls.readers} fallback={<span class="so-muted">niemand</span>}>{(reader) => (
                            <span class="so-chip">
                              {reader.firstName} {reader.lastName}
                              <button type="button" class="so-chip-x" title="Nicht mehr mitlesen" aria-label={`${reader.firstName} ${reader.lastName} nicht mehr mitlesen lassen`} disabled={assignBusy()} onClick={() => void changeReaders(() => props.api.removeReader(cls.id, reader.id), "Die Lehrkraft liest nicht mehr mit.")}>×</button>
                            </span>
                          )}</For>
                          <Show
                            when={readerTarget() === cls.id}
                            fallback={<button type="button" class="sr-linkbtn" style={{ "white-space": "nowrap" }} onClick={() => setReaderTarget(cls.id)}>+ hinzufügen</button>}
                          >
                            <select
                              class="form-input so-select"
                              disabled={assignBusy()}
                              onChange={(e) => {
                                const v = e.currentTarget.value;
                                if (v) void changeReaders(() => props.api.addReader(cls.id, v), "Die Lehrkraft liest jetzt mit.");
                              }}
                            >
                              <option value="">Lehrkraft wählen …</option>
                              <For each={overview().members.filter((m) => m.active && m.id !== cls.teacherId && !cls.readers.some((r) => r.id === m.id))}>{(m) => (
                                <option value={m.id}>{m.firstName} {m.lastName}{m.role === "owner" ? " (Schulleitung)" : ""}</option>
                              )}</For>
                            </select>
                            <button type="button" class="sr-linkbtn" onClick={() => setReaderTarget("")}>abbrechen</button>
                          </Show>
                        </span>
                      </div>
                    </div>
                  )}</For>
                </div>
              </Show>

              <p style={{ "margin-top": "16px" }}><button type="button" class="sr-linkbtn" onClick={props.onOpenClasses}>Meine eigenen Klassen</button></p>
            </>
          )}
        </Show>
      </Show>
    </div>
  );
}
